import { useCallback, useId, useMemo, useState } from 'react';

import BallotOutlinedIcon from '@mui/icons-material/BallotOutlined';
import {
  Box,
  ButtonBase,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import { DateRangeIcon } from '@mui/x-date-pickers';
import { format, isValid } from 'date-fns';
import { usePostHog } from 'posthog-js/react';
import { useNavigate } from 'react-router';

import ChartBoxMultipleOutline from '@/assets/icons/ChartBoxMultipleOutline';
import Button from '@/components/Buttons/Button';
import { ChartProvider } from '@/components/Chart/context/ChartContext';
import { exportChartImage } from '@/components/Chart/utils/exportChartImage';
import useDateText, { DateTextVariants } from '@/components/DateText/useDateText';
import { TABNAME_MAX_LENGTH } from '@/constants/name';
import { DataHubCard } from '@/features/DataHub/components/DataHubCard';
import { AccordionProvider } from '@/features/DataHub/components/ExportDrawer/AccordionContext';
import { ExportDrawerCancelButton } from '@/features/DataHub/components/ExportDrawer/ExportDrawerCancelButton';
import { ExportDrawerLayout } from '@/features/DataHub/components/ExportDrawer/ExportDrawerLayout';
import { ExportNameCard } from '@/features/DataHub/components/ExportDrawer/ExportNameCard';
import PreviewCustomChart from '@/features/DataHub/components/Preview/ChartPreviews/PreviewCustomChart';
import { useDataHubUserConfig } from '@/features/DataHub/hooks/useDataHubUserConfig';
import { DateRangePickers } from '@/features/DataHub/modules/Export/components/ExportSettings/DateRangePickers';
import NoDataDateAlert from '@/features/DataHub/modules/Export/components/ExportSettings/NoDataDateAlert';
import {
  PERIOD_OPTIONS_WITHOUT_VALUES,
  RangePeriodFields
} from '@/features/DataHub/modules/Export/components/ExportSettings/RangePeriodFields';
import { MIN_DATE } from '@/features/DataHub/modules/Export/constants/dateSettings';
import { validateExportNameWithMaxLength } from '@/features/DataHub/modules/Export/services/ExportNameService';
import {
  DateRangeType,
  type ExportSettingsType
} from '@/features/DataHub/modules/Export/types/context.types';
import {
  getDateRangeValidation,
  getRelativeRangeValue,
  parseSelectedRange,
  sanitizePeriodNumber
} from '@/features/DataHub/modules/Export/utils';
import { getPeriodLabel } from '@/features/DataHub/utils/getPeriodLabel';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import { closeDrawer } from '@/slices/drawerSlice';
import { deepMerge } from '@/utils/deepMerge';
import { buildDisplayDateSuffix } from '@/utils/exportFileDateSuffix';
import { parseFromToISO } from '@/utils/parseFromToISO';

import {
  buildCustomChartExportFileName,
  buildCustomChartInitialExportName
} from './customChartExportName';

import type { Chart, ExportingMimeTypeValue, Options } from 'highcharts';

const EXPORT_FONT_FAMILY = `'Cairo', Arial, sans-serif`;
const CAIRO_FONT_URL = '/fonts/Cairo.ttf';

const IMAGE_FORMATS: { label: string; type: ExportingMimeTypeValue }[] = [
  { label: 'PNG', type: 'image/png' },
  { label: 'PDF', type: 'application/pdf' },
  { label: 'JPEG', type: 'image/jpeg' },
  { label: 'SVG', type: 'image/svg+xml' }
];

const CustomChartExportViewInner = ({
  metadata,
  tabName,
  selectedRange
}: {
  metadata: Record<string, object | string>;
  tabName: string;
  selectedRange?: string;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const postHog = usePostHog();
  const [chartInstance, setChartInstance] = useState<Chart | null>(null);
  const { formatDateString } = useDateText();
  const { timezoneLabel, dateFormatValue } = useDataHubUserConfig();
  const radioGroupId = useId();
  const getDateSuffix = useCallback(() => {
    const nowIso = new Date().toJSON();
    const date = formatDateString(nowIso, DateTextVariants.FULL);
    return buildDisplayDateSuffix(date, nowIso);
  }, [formatDateString]);

  const [exportName, setExportName] = useState(() => buildCustomChartInitialExportName(tabName));
  const [selectedFormat, setSelectedFormat] = useState<ExportingMimeTypeValue>('image/png');
  const [hasPreviewData, setHasPreviewData] = useState<boolean | null>(null);

  const parsedInitialRange = useMemo(() => parseSelectedRange(selectedRange), [selectedRange]);
  const [dateRangeType, setDateRangeType] = useState<DateRangeType>(
    () => parsedInitialRange.dateRangeType
  );
  const [periodNumber, setPeriodNumber] = useState(() => parsedInitialRange.periodNumber);
  const [period, setPeriod] = useState<ExportSettingsType['period']>(
    () => parsedInitialRange.period
  );
  const [fromDate, setFromDate] = useState<Date | null>(() => parsedInitialRange.from);
  const [toDate, setToDate] = useState<Date | null>(() => parsedInitialRange.to);
  const exportNameError = useMemo(
    () => validateExportNameWithMaxLength(exportName, TABNAME_MAX_LENGTH),
    [exportName]
  );

  const fromISO = useMemo(() => {
    switch (dateRangeType) {
      case DateRangeType.ALL:
        return undefined;
      case DateRangeType.RANGE: {
        const from = parseFromToISO(getRelativeRangeValue(periodNumber, period));
        const fromDateValue = new Date(from);
        return Number.isNaN(fromDateValue.getTime()) ? undefined : fromDateValue.toISOString();
      }
      case DateRangeType.DATES:
        return fromDate?.toISOString();
      default:
        return undefined;
    }
  }, [dateRangeType, periodNumber, period, fromDate]);

  const toISO = useMemo(() => {
    if (dateRangeType !== DateRangeType.DATES) return undefined;
    return toDate?.toISOString();
  }, [dateRangeType, toDate]);

  const collapsedSummary = useMemo(() => {
    const tz = `Timezone: ${timezoneLabel}`;
    switch (dateRangeType) {
      case DateRangeType.ALL:
        return `All, ${tz}`;
      case DateRangeType.RANGE:
        return `Last ${periodNumber} ${getPeriodLabel(period, periodNumber)}, ${tz}`;
      case DateRangeType.DATES: {
        const fromStr = fromDate && isValid(fromDate) ? format(fromDate, dateFormatValue) : '';
        const toStr = toDate && isValid(toDate) ? format(toDate, dateFormatValue) : '';
        return `${fromStr} - ${toStr}, ${tz}`;
      }
      default:
        return '';
    }
  }, [dateRangeType, periodNumber, period, fromDate, toDate, timezoneLabel, dateFormatValue]);

  const onChangeDateRangeType = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as DateRangeType;
    setDateRangeType(value);
  }, []);

  const validation = useMemo(
    () => getDateRangeValidation({ dateRangeType, from: fromDate, to: toDate, periodNumber }),
    [dateRangeType, fromDate, toDate, periodNumber]
  );
  const hasDateSettingsError =
    validation.periodNumberError || validation.fromError || validation.toError;
  const showNoDataDateAlert = hasPreviewData === false && !hasDateSettingsError;

  const handleExport = useCallback(() => {
    if (!chartInstance || exportNameError || hasDateSettingsError) return;

    const dateSuffix = getDateSuffix();
    const fileName = buildCustomChartExportFileName(exportName, dateSuffix);
    const baseChartOptions = chartInstance.options.exporting?.chartOptions ?? chartInstance.options;
    const exportOptions = deepMerge({} as Options, baseChartOptions);
    exportOptions.chart = {
      ...exportOptions.chart,
      style: {
        ...exportOptions.chart?.style,
        fontFamily: EXPORT_FONT_FAMILY
      }
    };
    exportOptions.legend = {
      ...exportOptions.legend,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      padding: 20,
      itemWidth: undefined,
      itemStyle: {
        ...exportOptions.legend?.itemStyle,
        fontSize: '10px',
        textOverflow: undefined
      }
    };

    const axisLabelStyle = { fontSize: '9px' };
    const withAxisFontSize = (axis: unknown): unknown => {
      if (Array.isArray(axis)) {
        return axis.map(a => ({
          ...a,
          labels: { ...a?.labels, style: { ...a?.labels?.style, ...axisLabelStyle } }
        }));
      }
      const a = axis as Record<string, unknown> | undefined;
      return {
        ...a,
        labels: {
          ...(a?.labels as object),
          style: { ...(a?.labels as any)?.style, ...axisLabelStyle }
        }
      };
    };
    exportOptions.xAxis = withAxisFontSize(exportOptions.xAxis) as typeof exportOptions.xAxis;
    exportOptions.yAxis = withAxisFontSize(exportOptions.yAxis) as typeof exportOptions.yAxis;

    const exportingOptions =
      selectedFormat === 'application/pdf'
        ? {
            local: true,
            sourceWidth: 1200,
            pdfFont: {
              normal: CAIRO_FONT_URL,
              bold: CAIRO_FONT_URL,
              italic: CAIRO_FONT_URL,
              bolditalic: CAIRO_FONT_URL
            }
          }
        : { sourceWidth: 1200 };

    postHog?.capture('chart_download_button_click', { format: selectedFormat, tabName });

    exportChartImage({
      chart: chartInstance,
      type: selectedFormat,
      filename: fileName,
      options: exportOptions,
      exportingOptions
    });
  }, [
    chartInstance,
    exportName,
    exportNameError,
    getDateSuffix,
    hasDateSettingsError,
    postHog,
    selectedFormat,
    tabName
  ]);

  return (
    <ExportDrawerLayout
      footer={
        <>
          <ExportDrawerCancelButton />
          <Button
            disabled={
              !chartInstance ||
              hasPreviewData !== true ||
              Boolean(exportNameError) ||
              hasDateSettingsError
            }
            variant='contained'
            onClick={handleExport}
          >
            Export image
          </Button>
        </>
      }
    >
      <AccordionProvider>
        <ExportNameCard
          exportName={exportName}
          exportNameError={exportNameError}
          onChange={setExportName}
        />

        <DataHubCard
          expandable
          collapsedSummary={collapsedSummary}
          icon={<DateRangeIcon sx={{ fontSize: '16px' }} />}
          subtitle='Select date range and timezone options'
          title='Export settings'
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              fontSize: '14px',
              my: 1.5
            }}
          >
            <Divider sx={{ mb: 2 }} />
            {showNoDataDateAlert && <NoDataDateAlert />}
            <Typography sx={{ mb: 1.5, fontSize: 14 }}>Date settings</Typography>
            <FormControl>
              <RadioGroup
                aria-labelledby={radioGroupId}
                value={dateRangeType}
                onChange={onChangeDateRangeType}
              >
                <FormControlLabel
                  control={<Radio />}
                  label='All'
                  value={DateRangeType.ALL}
                />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: 50,
                    marginBottom: validation.periodNumberError ? 2 : 0
                  }}
                >
                  <FormControlLabel
                    control={<Radio />}
                    label='Last'
                    value={DateRangeType.RANGE}
                  />
                  <RangePeriodFields
                    period={period}
                    periodNumber={periodNumber}
                    periodNumberError={validation.periodNumberError}
                    periodOptions={PERIOD_OPTIONS_WITHOUT_VALUES}
                    onChangePeriod={e => {
                      setPeriod(e.target.value);
                    }}
                    onChangePeriodNumber={e => {
                      setPeriodNumber(sanitizePeriodNumber(e.target.value));
                    }}
                  />
                </Box>
                <FormControlLabel
                  control={<Radio />}
                  label='From'
                  value={DateRangeType.DATES}
                />
                <DateRangePickers
                  dateFormat={dateFormatValue}
                  from={fromDate}
                  fromError={validation.fromError}
                  fromErrorMessage={validation.fromErrorMessage}
                  isDatePickerDisabled={dateRangeType !== DateRangeType.DATES}
                  maxDate={new Date()}
                  minDate={MIN_DATE}
                  to={toDate}
                  toError={validation.toError}
                  toErrorMessage={validation.toErrorMessage}
                  onChange={(field, value) => {
                    if (field === 'from') setFromDate(value);
                    else setToDate(value);
                  }}
                />
              </RadioGroup>
            </FormControl>
            <Divider sx={{ my: 2 }} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline'
              }}
            >
              Timezone: {timezoneLabel}{' '}
              <ButtonBase
                sx={{ ml: 2, color: 'primary.main' }}
                onClick={() => {
                  navigate('/profile', { replace: true });
                  dispatch(closeDrawer());
                }}
              >
                Change in Settings
              </ButtonBase>
            </Box>
            <Divider sx={{ mt: 2 }} />
          </Box>
        </DataHubCard>

        <DataHubCard
          expandable={false}
          icon={<ChartBoxMultipleOutline sx={{ fontSize: '16px' }} />}
          subtitle='Preview visualization before exporting'
          title='Preview'
        >
          <PreviewCustomChart
            from={fromISO}
            metadata={metadata}
            to={toISO}
            onChartReady={setChartInstance}
            onHasDataChange={setHasPreviewData}
          />
        </DataHubCard>

        <DataHubCard
          expandable={false}
          icon={<BallotOutlinedIcon sx={{ fontSize: '16px' }} />}
          subtitle='Select image format'
          title='Format'
        >
          <Box
            sx={{
              maxHeight: 150,
              fontSize: 16,
              fontWeight: 600,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              flexWrap: 'wrap'
            }}
          >
            {IMAGE_FORMATS.map(({ label, type }) => (
              <Box
                key={label}
                sx={{ mt: 1 }}
              >
                <ButtonBase
                  sx={{
                    border: '1px solid',
                    borderColor: selectedFormat === type ? 'white' : 'transparent',
                    backgroundColor: 'secondary.main',
                    justifyContent: 'flex-start',
                    px: 1,
                    py: 0.5,
                    width: '100%'
                  }}
                  onClick={() => {
                    setSelectedFormat(type);
                  }}
                >
                  {label}
                </ButtonBase>
              </Box>
            ))}
          </Box>
        </DataHubCard>
      </AccordionProvider>
    </ExportDrawerLayout>
  );
};

export const CustomChartExportView = () => {
  const customChartExportContext = useAppSelector(state => state.drawer.customChartExportContext);

  if (!customChartExportContext) return null;

  return (
    <ChartProvider>
      <CustomChartExportViewInner
        metadata={customChartExportContext.metadata}
        selectedRange={customChartExportContext.selectedRange}
        tabName={customChartExportContext.tabName}
      />
    </ChartProvider>
  );
};
